/**
 * Minimal fetch wrapper replacing axios.
 *
 * axios rejects on non-2xx responses and exposes the parsed body as
 * `error.response.data`. `HttpError.data` fills the same role so the existing
 * `ErrorService.getError(..., data.errorCode)` handling keeps working.
 *
 * `credentials` defaults to 'same-origin' in fetch, matching axios' default.
 */
export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly data: unknown,
  ) {
    super(`Request failed with status code ${status}`);
    this.name = 'HttpError';
  }
}

function parseBody(text: string): unknown {
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const data = parseBody(await response.text());

  if (!response.ok) {
    throw new HttpError(response.status, data);
  }

  return data as T;
}

export function getJson<T>(url: string): Promise<T> {
  return request<T>(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
}

export function postJson<T>(url: string, body?: unknown): Promise<T> {
  return request<T>(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function postForm<T>(url: string, body?: string): Promise<T> {
  return request<T>(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
    },
    body,
  });
}

/** Extracts an errorCode from an unknown thrown value, mirroring the old `e.response.data.errorCode`. */
export function errorCodeOf(error: unknown): unknown {
  if (error instanceof HttpError && error.data && typeof error.data === 'object') {
    return (error.data as { errorCode?: unknown }).errorCode;
  }
  return undefined;
}
